import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

/* ---------------------------------------------------------------------------
 * Move-to-Group dialog: converts an independent pub into a member of an
 * existing pub group. Backend `/api/admin/move-pub-to-group` handles the
 * dry-run + actual reassignment (see admin.py). We show the dry-run plan
 * first so the admin can eyeball the impact (which client will be deleted,
 * which login gets removed) before confirming.
 * ------------------------------------------------------------------------- */
export function MoveToGroupButton({ pub, pubGroups, onMoved }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("");
  const [deleteOldUser, setDeleteOldUser] = useState(true);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setPlan(null); setTarget(""); setDeleteOldUser(true); };

  const preview = async () => {
    if (!target) { toast.error("Pick a pub group first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/move-pub-to-group", {
        pub_slug: pub.slug,
        target_client_id: target,
        delete_old_client_user: deleteOldUser,
        dry_run: true,
      });
      setPlan(data.plan);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/admin/move-pub-to-group", {
        pub_slug: pub.slug,
        target_client_id: target,
        delete_old_client_user: deleteOldUser,
        dry_run: false,
      });
      const groupName = pubGroups.find((g) => g.id === target)?.name || "the group";
      toast.success(`${pub.name} moved into ${groupName}.`);
      setOpen(false);
      reset();
      onMoved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="move-to-group-open"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-300 hover:border-zinc-950 rounded-sm px-3 h-10"
      >
        <Building2 className="h-3.5 w-3.5" /> Move to pub group…
      </button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="bg-white max-w-lg" data-testid="move-to-group-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-zinc-950">
              Move {pub.name} to a pub group
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Reassigns this pub&apos;s client_id to an existing pub group. The pub keeps its slug, prize, sponsors, and every prediction — only its parent client changes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Target pub group</Label>
              <Select value={target} onValueChange={(v) => { setTarget(v); setPlan(null); }}>
                <SelectTrigger className="mt-2 h-10 rounded-sm border-zinc-300" data-testid="move-to-group-select">
                  <SelectValue placeholder="Pick a pub group…" />
                </SelectTrigger>
                <SelectContent>
                  {pubGroups.map((g) => (
                    <SelectItem key={g.id} value={g.id} data-testid={`move-to-group-option-${g.id}`}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer">
              <input
                type="checkbox"
                checked={deleteOldUser}
                onChange={(e) => { setDeleteOldUser(e.target.checked); setPlan(null); }}
                data-testid="move-to-group-delete-user"
                className="mt-0.5"
              />
              <span>
                <span className="font-semibold text-zinc-950">Delete the old owner login.</span>
                {" "}Recommended. Untick to keep the login but detach it (they lose dashboard access).
              </span>
            </label>

            {plan && (
              <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="move-to-group-plan">
                <p className="font-semibold mb-1">Dry run — this is what will happen:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li><span className="font-semibold">{plan.pub.name}</span> ({plan.pub.slug}) → <span className="font-semibold">{plan.to_client.name}</span> (pub group).</li>
                  <li>
                    Old client <span className="font-semibold">{plan.from_client.name}</span>
                    {plan.from_client.will_be_deleted ? " will be DELETED (has no other pubs)." : " will NOT be deleted (still has other pubs)."}
                  </li>
                  {plan.old_client_users.length > 0 && (
                    <li>
                      {deleteOldUser ? "Deletes" : "Detaches"} {plan.old_client_users.length} old client login(s):
                      {" "}{plan.old_client_users.map((u) => u.email).join(", ")}.
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => { setOpen(false); reset(); }}
              data-testid="move-to-group-cancel"
              className="h-10 rounded-sm"
            >
              Cancel
            </Button>
            {!plan ? (
              <Button
                onClick={preview}
                disabled={!target || busy}
                data-testid="move-to-group-preview"
                className="h-10 rounded-sm bg-zinc-950 hover:bg-black text-white"
              >
                {busy ? "Checking…" : "Preview move"}
              </Button>
            ) : (
              <Button
                onClick={confirm}
                disabled={busy}
                data-testid="move-to-group-confirm"
                className="h-10 rounded-sm bg-red-600 hover:bg-red-700 text-white"
              >
                {busy ? "Moving…" : "Confirm move"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
